/** Shared portal rule: registration, reset password, and any other “set password” UI must stay in sync with this. */

export const PASSWORD_REQUIREMENT_ERROR_MESSAGE =
  "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.";

export type PasswordRequirementFeedback = {
  intro: string | null;
  strengthLabel: string | null;
  stillNeededCaption: string | null;
  missingBullets: string[];
  className: string;
};

export function isStrongPassword(s: string): boolean {
  const t = s.trim();
  if (t.length < 8) return false;
  const hasUpper = /[A-Z]/.test(t);
  const hasLower = /[a-z]/.test(t);
  const hasDigit = /\d/.test(t);
  const hasSpecial = /[^a-zA-Z0-9]/.test(t);
  return hasUpper && hasLower && hasDigit && hasSpecial;
}

/** Live feedback for the password field (same bullets / wording everywhere). */
export function getPasswordRequirementFeedback(passwordInput: string): PasswordRequirementFeedback {
  const trimmedPassword = passwordInput.trim();
  const requirementOrder = ["minLength", "hasUpper", "hasLower", "hasDigit", "hasSpecial"] as const;
  const requirementLabels: Record<(typeof requirementOrder)[number], string> = {
    minLength: "At least 8 characters",
    hasUpper: "One uppercase letter (A–Z)",
    hasLower: "One lowercase letter (a–z)",
    hasDigit: "One number (0–9)",
    hasSpecial: "One special character (for example ! @ # $ % ^)",
  };

  if (!trimmedPassword) {
    return {
      intro: "Your password must include:",
      strengthLabel: null,
      stillNeededCaption: null,
      missingBullets: requirementOrder.map((key) => requirementLabels[key]),
      className: "text-secondary",
    };
  }

  const checks = {
    minLength: trimmedPassword.length >= 8,
    hasUpper: /[A-Z]/.test(trimmedPassword),
    hasLower: /[a-z]/.test(trimmedPassword),
    hasDigit: /\d/.test(trimmedPassword),
    hasSpecial: /[^a-zA-Z0-9]/.test(trimmedPassword),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const missingBullets = requirementOrder.filter((key) => !checks[key]).map((key) => requirementLabels[key]);

  if (score === 5) {
    return {
      intro: null,
      strengthLabel: "Strong password.",
      stillNeededCaption: null,
      missingBullets: [],
      className: "text-success",
    };
  }

  if (score >= 3) {
    return {
      intro: null,
      strengthLabel: "Medium password",
      stillNeededCaption: missingBullets.length > 0 ? "Still needed:" : null,
      missingBullets,
      className: "text-warning",
    };
  }

  return {
    intro: null,
    strengthLabel: "Weak password",
    stillNeededCaption: missingBullets.length > 0 ? "Still needed:" : null,
    missingBullets,
    className: "text-danger",
  };
}
